import {PermissionType} from '@hconnect/apiclient'
import {FILE_MAX_SIZE_IN_BYTES, MAX_ALLOWED_ATTACHMENTS} from '@hconnect/common/consts'
import {headlineSx} from '@hconnect/common/styles'
import {AttachmentFile} from '@hconnect/common/types'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {
  type AllowedFileFormat,
  type AttachementUploadConfig,
  AttachmentUpload
} from '@widgets/common-attachments'
import React, {Dispatch, SetStateAction, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_SHIFT_EVENT_WIDGET_NAMESPACE} from '../consts'

const ALLOWED_IMAGE_FORMATS: AllowedFileFormat[] = ['.png', '.jpg', '.jpeg']
const ALLOWED_WORKSHEET_FORMATS: AllowedFileFormat[] = ['.xlsx', '.xls']
const ALLOWED_VIDEO_FORMATS: AllowedFileFormat[] = ['.mp4', '.3gp', '.mov']
const ALLOWED_DOCUMENT_FORMATS: AllowedFileFormat[] = ['.doc', '.docx']

export const EventAttachmentUpload: React.FC<{
  attachments: Array<AttachmentFile>
  setAttachments: Dispatch<SetStateAction<AttachmentFile[]>>
  isMovieAttachmentEnabled?: boolean
  hasPermission: (permission: PermissionType) => boolean
  additionalCount?: number
}> = ({attachments, setAttachments, isMovieAttachmentEnabled, hasPermission, additionalCount}) => {
  const {t} = useTranslation(COMMON_SHIFT_EVENT_WIDGET_NAMESPACE)
  const supportedFormats: AllowedFileFormat[] = useMemo(
    () => [
      '.pdf',
      ...ALLOWED_IMAGE_FORMATS,
      ...ALLOWED_WORKSHEET_FORMATS,
      ...ALLOWED_DOCUMENT_FORMATS,
      ...(isMovieAttachmentEnabled ? ALLOWED_VIDEO_FORMATS : [])
    ],
    [isMovieAttachmentEnabled]
  )

  const attachmentUploadConfig = useMemo(
    (): AttachementUploadConfig => ({
      allowedFormats: supportedFormats,
      maxAttachements: MAX_ALLOWED_ATTACHMENTS,
      maxFileSizeMB: FILE_MAX_SIZE_IN_BYTES / (1024 * 1024),
      isAddingEnabled: hasPermission('ADD_DOCUMENTS'),
      isDeletingEnabled: hasPermission('DELETE_DOCUMENTS')
    }),
    [supportedFormats, hasPermission]
  )

  const attachmentCount = (attachments?.length ?? 0) + (additionalCount ?? 0)

  return (
    <AttachmentUpload
      attachments={attachments}
      setAttachments={setAttachments}
      config={attachmentUploadConfig}
      attachmentsTitle={
        <CardSectionTitle>
          {`${t('shiftEvent.label.attachments')} ${attachmentCount}/${MAX_ALLOWED_ATTACHMENTS}`}
        </CardSectionTitle>
      }
      containerSx={headlineSx()}
    />
  )
}
